import { useEffect } from "react";
import { Tooltip } from "antd";
import AddToWaitlistModal from "components/modals/AddToWaitlistModal.component";
import NewBookingModal from "components/modals/NewBookingModal.component";
import { translate } from "shared/languages";
import { WAITLIST_CAP } from "shared/constants/waitlist";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { isWaitlistClosed, waitlistCutoffMinutesOf } from "shared/utils/cutoff";

interface PrimaryActionProps {
  scheduleId: string;
  occurrenceTimestamp: string;
  classId: string;
  isPrivate: boolean;
  /** Schedule is deleted/tombstoned (schedule.is_cancelled) — blocks all adds. */
  locked: boolean;
  hasStarted?: boolean;
  confirmedCount: number;
  capacity: number;
  waitlistLength: number;
  onWaitlistAdded?: () => void;
  // Resuming after a round trip to a client's detail page to buy a package
  // (see ScheduleDetails.tsx's resume effect). Forwarded to whichever modal
  // is currently rendered below — if capacity changed while the admin was
  // away and the other modal renders instead, it still picks this up, since
  // both accept the same prop.
  resumeCustomerId?: string;
  onResumeConsumed?: () => void;
}

const PrimaryAction = ({
  scheduleId,
  occurrenceTimestamp,
  classId,
  isPrivate,
  locked,
  hasStarted = false,
  confirmedCount,
  capacity,
  waitlistLength,
  onWaitlistAdded,
  resumeCustomerId,
  onResumeConsumed,
}: PrimaryActionProps) => {
  // Configured per-business policy (source of truth); fall back to the defaults
  // only while the settings query is in flight.
  const { data: settings } = useBusinessSettings();
  const waitlistCapacity = settings?.waitlist_capacity ?? WAITLIST_CAP;

  const classFull = confirmedCount >= capacity;
  // The studio switched its waitlist off (Pengaturan -> Kebijakan Booking &
  // Jadwal): a full class has nothing to add to.
  const waitlistOff = settings?.waitlist_enabled === false;
  const waitlistFull = waitlistLength >= waitlistCapacity;
  // The waitlist closes at the auto-promote cutoff (hours or minutes before
  // start, shared/utils/cutoff.ts), not only once the class has started —
  // mirrors the server gate so the admin sees the closed state immediately
  // instead of discovering it via an error.
  const waitlistClosed =
    hasStarted ||
    isWaitlistClosed(occurrenceTimestamp, waitlistCutoffMinutesOf(settings));

  // A package bought on the way to Tambah ke Waitlist (the Kelola Paket round
  // trip) has no modal to come back to once the waitlist is off: drop it
  // rather than open a booking on a full class.
  const dropResume = classFull && waitlistOff && !!resumeCustomerId;
  useEffect(() => {
    if (dropResume) onResumeConsumed?.();
  }, [dropResume, onResumeConsumed]);

  // Private classes have a fixed roster set at schedule creation — there is no
  // add-booking action for them at all.
  if (isPrivate) {
    return null;
  }

  // Open class with seats free: admins may add a booking at any point in the
  // schedule's lifecycle (incl. Ongoing / Finished, for backfilling
  // attendance). Only a deleted schedule blocks it.
  if (!classFull) {
    return (
      <NewBookingModal
        scheduleId={scheduleId}
        occurrenceTimestamp={occurrenceTimestamp}
        classId={classId}
        disabled={locked}
        resumeCustomerId={resumeCustomerId}
        onResumeConsumed={onResumeConsumed}
      />
    );
  }

  if (waitlistOff) {
    return (
      <Tooltip title={translate("messages.error.class_full_no_waitlist")} placement="topRight">
        <span>
          <NewBookingModal
            scheduleId={scheduleId}
            occurrenceTimestamp={occurrenceTimestamp}
            classId={classId}
            disabled
          />
        </span>
      </Tooltip>
    );
  }

  const button = (
    <AddToWaitlistModal
      scheduleId={scheduleId}
      occurrenceTimestamp={occurrenceTimestamp}
      classId={classId}
      confirmedCount={confirmedCount}
      capacity={capacity}
      waitlistLength={waitlistLength}
      disabled={locked || waitlistClosed || waitlistFull}
      onAdded={onWaitlistAdded}
      resumeCustomerId={resumeCustomerId}
      onResumeConsumed={onResumeConsumed}
    />
  );

  if (waitlistClosed) {
    return (
      <Tooltip
        placement="topRight"
        title={translate(
          hasStarted
            ? "messages.error.class_already_started"
            : "messages.error.waitlist_closed",
        )}
      >
        <span>{button}</span>
      </Tooltip>
    );
  }
  if (waitlistFull) {
    return (
      <Tooltip title={translate("messages.error.class_and_waitlist_full")} placement="topRight">
        <span>{button}</span>
      </Tooltip>
    );
  }
  return button;
};

export default PrimaryAction;
